'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useLoginHref } from '../_hooks/useLoginHref';
import { useLottoPool } from '../lucky-ticket/_hooks/useLottoPool';
const PoolAmountPanel = t0 => {
  const $ = _c(8);
  const {
    accumulatedLabel,
    remainingLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-Gray-600 typo-caption1">누적 금액</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== accumulatedLabel) {
    t2 = <p className="flex items-center gap-6 font-600">{t1}<span className="tabular-nums text-Gray-900 typo-body4">{accumulatedLabel}</span></p>;
    $[1] = accumulatedLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== remainingLabel) {
    t3 = remainingLabel ? <p className="font-400 tabular-nums text-Gray-500 typo-caption1">{remainingLabel}</p> : <Badge className="bg-M-400 text-Gray-white">목표금액 달성!</Badge>;
    $[3] = remainingLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex items-center justify-between gap-8 whitespace-nowrap rounded-small bg-Gray-white/70 p-12">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const PoolAmount = () => {
  const $ = _c(8);
  const {
    data
  } = useLottoPool();
  const remainingAmount = data.targetAmount - data.accumulatedAmount;
  let t0;
  if ($[0] !== data.accumulatedAmount) {
    t0 = data.accumulatedAmount.toLocaleString();
    $[0] = data.accumulatedAmount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = `${t0}원`;
  let t2;
  if ($[2] !== data.potGauge.isGoalReached || $[3] !== remainingAmount) {
    t2 = data.potGauge.isGoalReached ? undefined : `목표금액까지 ${Math.max(remainingAmount, 0).toLocaleString()}원`;
    $[2] = data.potGauge.isGoalReached;
    $[3] = remainingAmount;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <PoolAmountPanel accumulatedLabel={t1} remainingLabel={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const PoolAmountFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PoolAmountPanel accumulatedLabel="-" remainingLabel={"\uBAA9\uD45C\uAE08\uC561\uAE4C\uC9C0 -"} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const GuestLoginButton = () => {
  const $ = _c(2);
  const loginHref = useLoginHref();
  let t0;
  if ($[0] !== loginHref) {
    t0 = <Button asChild={true} theme="sub-line" dimensions="H40" className="w-full"><CustomLink href={loginHref}>로그인하기</CustomLink></Button>;
    $[0] = loginHref;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const CARD_CLASS_NAME = '-mx-20 -mb-4 mt-4 flex flex-col gap-12 bg-gradient-to-r from-Y-50 to-G-25 p-16';
const CARD_FEEDBACK_CLASS_NAME = cn('transition-opacity hover:opacity-80 active:opacity-80', FOCUS_RING);

//* 누적 금액은 공동 값(인증 불요)이지만 시안이 비회원 자리에 로그인 버튼을 둔다 — 응모는 허브에서 한다.
export const LuckyLottoCard = t0 => {
  const $ = _c(6);
  const {
    isGuest: serverIsGuest
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src="/assets/pngs/benefit-lucky-ticket/lotto-gift.png" alt="" width={50} height={50} sizes="50px" className="size-50 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-12">{t1}<div className="flex flex-1 flex-col gap-4"><p className="font-700 text-Gray-900 typo-body3">행운의 로또</p><p className="break-keep font-500 text-Gray-700 typo-body4">목표금액 달성하고 로또 응모해요!</p></div><SvgChevronright className="size-16 shrink-0 text-Gray-600 transition group-hover:translate-x-1 group-hover:text-Gray-700 group-focus-visible:translate-x-1 group-focus-visible:text-Gray-700" /></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const heading = t2;
  if (isGuest) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className={CARD_CLASS_NAME}><CustomLink href={ROUTES.BENEFIT_LUCKY_TICKET.pathname} className={cn("group rounded-xsmall", CARD_FEEDBACK_CLASS_NAME)}>{heading}</CustomLink><GuestLoginButton /></div>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group", CARD_CLASS_NAME, CARD_FEEDBACK_CLASS_NAME);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PoolAmountFallback />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CustomLink href={ROUTES.BENEFIT_LUCKY_TICKET.pathname} className={t3}>{heading}<ErrorBoundary fallback={t4}><Suspense fallback={<PoolAmountFallback />}><PoolAmount /></Suspense></ErrorBoundary></CustomLink>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};